import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, XCircleIcon, InfoIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SiteFooter } from '../components/SiteFooter';
import { GRITVUE_BATCH_AUTH_CODE, normalizeCode } from '../data/authenticity';
import { trackCustom } from '../utils/tracking';
type Status = 'idle' | 'verified' | 'mismatch' | 'unavailable';
const nextSteps = [{
  title: 'Need a backup X1?',
  body: 'Keep a spare charged in the truck.',
  to: '/#offer'
}, {
  title: 'Equip your crew',
  body: 'Business / crew pack, one order.',
  to: '/#company'
}, {
  title: 'Refer a friend & earn',
  body: 'GritVue Crew Rewards.',
  to: '/rewards'
}];
export function Authenticity() {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  useEffect(() => {
    window.scrollTo(0, 0);
    trackCustom('authenticity_page_visited');
  }, []);
  const verify = (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim()) return;
    trackCustom('batch_verification_attempted');
    if (!GRITVUE_BATCH_AUTH_CODE) {
      setStatus('unavailable');
      return;
    }
    if (normalizeCode(code) === normalizeCode(GRITVUE_BATCH_AUTH_CODE)) {
      setStatus('verified');
      trackCustom('batch_verification_successful');
    } else {
      setStatus('mismatch');
    }
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1664" data-mp="3knpiqfj5ds43">
      <PageHeader data-id="element-1665" data-mp="3klmnnh3518si" />
      <main className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:grid lg:grid-cols-[1fr_1fr] lg:gap-16 lg:px-8 lg:py-20" data-id="element-1666" data-mp="377q5bizgqcs">
        <div data-id="element-1667" data-mp="3ullt3549gj15">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-1668" data-mp="3ul3pfnf9r2j7">Batch authenticity check</p>
          <h1 className="grit-headline mt-3 text-[clamp(2.5rem,8vw,4.75rem)] font-extrabold uppercase leading-[0.92]" data-id="element-1669" data-mp="36uq40fr6w1xl">
            Verify your
            <br data-id="element-1670" data-mp="15ubhp4xx8hsc" />
            <span className="text-grit-500" data-id="element-1671" data-mp="15uv2quivbmal">GritVue.</span>
          </h1>
          <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-steel-300 sm:text-lg" data-id="element-1672" data-mp="1at6b10tiloqp">
            Enter the code printed on the authenticity card in your box. We&apos;ll confirm it matches an authorized
            GritVue X1 production batch.
          </p>
          <p className="mt-4 flex max-w-lg items-start gap-2 text-[13px] leading-relaxed text-steel-500" data-id="element-1673" data-mp="1jkoq18eq5yi7">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1674" data-mp="mxacbj0ggfnv" />
            This checks the production batch. A batch code is shared by the units in that batch, so it does not identify
            one individual unit.
          </p>
        </div>

        <div className="mt-10 lg:mt-0" data-id="element-1675" data-mp="10w1m4t8abf67">
          <form onSubmit={verify} className="border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-1676" data-mp="10svylifb0lcl">
            <label htmlFor="batch-code" className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1677" data-mp="27asqj6wm9i4z">
              Authenticity card code
            </label>
            <input id="batch-code" value={code} onChange={event => {
            setCode(event.target.value);
            if (status !== 'idle') setStatus('idle');
          }} autoComplete="off" autoCapitalize="characters" placeholder="Enter code" className="mt-2 w-full border-2 border-white/20 bg-ink-950 px-4 py-3.5 font-display text-2xl font-bold uppercase tracking-[0.12em] text-offwhite placeholder:text-steel-500 focus:border-grit-500 focus:outline-none" data-id="element-1678" data-mp="2mcr8p7tlyg7t" />
            <button type="submit" disabled={!code.trim()} className="mt-4 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1679" data-mp="1tqg83x604fb0">
              Check my code
            </button>

            <AnimatePresence mode="wait" data-id="element-1680" data-mp="3w2ssk8rzdzuo">
              {status !== 'idle' && <motion.div key={status} role="status" initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.18,
              ease: [0.23, 1, 0.32, 1]
            }} className="mt-5" data-id="element-1681" data-mp="3i3b2lq2y8gjq">
                  {status === 'verified' && <div className="border-2 border-grit-500 bg-grit-500/10 p-5" data-id="element-1682" data-mp="2yhohlekdwo1x">
                      <p className="flex items-center gap-2 font-display text-2xl font-extrabold uppercase text-grit-500" data-id="element-1683" data-mp="33eoplv0so0vo">
                        <BadgeCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-1684" data-mp="2ph05ipejwxro" />
                        Verified GritVue production batch
                      </p>
                      <p className="mt-2 text-[15px] text-steel-200" data-id="element-1685" data-mp="2asgkb717eog5">
                        Your code matches an authorized GritVue X1 production batch. Welcome to GritVue.
                      </p>
                    </div>}
                  {status === 'mismatch' && <div className="border border-white/20 bg-white/[0.03] p-5" data-id="element-1686" data-mp="2fsbcrf0mnm3b">
                      <p className="flex items-center gap-2 font-display text-xl font-bold uppercase text-offwhite" data-id="element-1687" data-mp="2trugzwqbidac">
                        <XCircleIcon className="h-6 w-6 text-steel-400" aria-hidden="true" data-id="element-1688" data-mp="2ot9fzv38rpku" />
                        We couldn&apos;t match that code
                      </p>
                      <p className="mt-2 text-[14px] text-steel-300" data-id="element-1689" data-mp="33jgnxgvmhq7a">
                        Check it against your card and try again. Still no match? Email support@gritvuegear.com with a
                        photo of the card and where you bought it.
                      </p>
                    </div>}
                  {status === 'unavailable' && <div className="border border-white/20 bg-white/[0.03] p-5" data-id="element-1690" data-mp="257nv6yymiyej">
                      <p className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-1691" data-mp="2a5pg7pfm17sn">Batch verification is being set up</p>
                      <p className="mt-2 text-[14px] text-steel-300" data-id="element-1692" data-mp="2e4es1dl02ha6">
                        Email your code and a photo of the card to support@gritvuegear.com and we&apos;ll confirm it for
                        you.
                      </p>
                    </div>}
                </motion.div>}
            </AnimatePresence>
          </form>

          {status === 'verified' && <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-1693" data-mp="31w3q7aki5zz1">
              {nextSteps.map(item => <li key={item.title} data-id="element-1694" data-mp="13gqv41bkpths">
                  <Link to={item.to} className="group flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1695" data-mp="13hsjf7hlochg">
                    <span data-id="element-1696" data-mp="v837x5x2sn8v">
                      <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1697" data-mp="v6s5paja3njv">
                        {item.title}
                      </span>
                      <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1698" data-mp="m7y2v2fcu0xv">{item.body}</span>
                    </span>
                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1699" data-mp="1iw4g2dqce1nr" />
                  </Link>
                </li>)}
              <li data-id="element-1700" data-mp="19whbxk68ahr0">
                <a href="/#rx" className="group flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1701" data-mp="19vy44waal9qp">
                  <span data-id="element-1702" data-mp="3ukltw0x5a8io">
                    <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1703" data-mp="3po55d5m19s52">
                      Add RX capability
                    </span>
                    <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1704" data-mp="1tn3cwzi20fbw">Available now: RX Capability Insert, +$14.95</span>
                  </span>
                  <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1705" data-mp="1jnuosn9vduxg" />
                </a>
              </li>
            </ul>}
        </div>
      </main>
      <SiteFooter data-id="element-1706" data-mp="2cc9qy97wkird" />
    </div>;
}